import * as $bit_array from "../../gleam_stdlib/gleam/bit_array.mjs";
import * as $bool from "../../gleam_stdlib/gleam/bool.mjs";
import * as $dict from "../../gleam_stdlib/gleam/dict.mjs";
import * as $float from "../../gleam_stdlib/gleam/float.mjs";
import * as $int from "../../gleam_stdlib/gleam/int.mjs";
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $order from "../../gleam_stdlib/gleam/order.mjs";
import { Eq, Gt, Lt } from "../../gleam_stdlib/gleam/order.mjs";
import * as $pair from "../../gleam_stdlib/gleam/pair.mjs";
import * as $set from "../../gleam_stdlib/gleam/set.mjs";
import * as $string from "../../gleam_stdlib/gleam/string.mjs";
import * as $yielder from "../../gleam_yielder/gleam/yielder.mjs";
import {
  Ok,
  Error,
  toList,
  Empty as $Empty,
  prepend as listPrepend,
  CustomType as $CustomType,
  makeError,
} from "../gleam.mjs";
import * as $seed from "../prng/seed.mjs";
import { random_int, random_float } from "../prng_ffi.mjs";

const FILEPATH = "src/prng/random.gleam";

class Generator extends $CustomType {
  constructor(step) {
    super();
    this.step = step;
  }
}

/**
 * The underlying algorith will work best for integers in the inclusive range
 * going from `min_int` up to `max_int`.
 *
 * It can generate values outside of that range, but they are "not as random".
 */
export const min_int = -2_147_483_648;

/**
 * The underlying algorith will work best for integers in the inclusive range
 * going from `min_int` up to `max_int`.
 *
 * It can generate values outside of that range, but they are "not as random".
 */
export const max_int = 2_147_483_647;

/**
 * Steps a `Generator(a)` producing a random value of type `a` using the given
 * seed as the source of randomness.
 *
 * The stepping logic is completely deterministic. This means that, given a
 * seed and a generator, you'll always get the same result.
 *
 * This is why this function also returns a new seed that can be used to make
 * subsequent calls to `step` to get other random values.
 *
 * Stepping a generator by hand can be quite cumbersome, so I recommend you
 * try [`to_yielder`](#to_yielder),
 * [`to_random_yielder`](#to_random_yielder), or [`sample`](#sample) instead.
 *
 * ## Examples
 *
 * ```gleam
 * let initial_seed = seed.new(11)
 * let dice_roll = random.int(1, 6)
 * let #(first_roll, new_seed) = random.step(dice_roll, initial_seed)
 * let #(second_roll, _) = random.step(dice_roll, new_seed)
 *
 * #(first_roll, second_roll)
 * // -> #(3, 2)
 * ```
 */
export function step(generator, seed) {
  return generator.step(seed);
}

/**
 * Generates a single value using the given generator and seed.
 *
 * This is just a shorthand for the `step` function that drops the new
 * seed. It can be useful if you just need to get a single value out of
 * a generator and need the result to be reproducible.
 */
export function sample(generator, seed) {
  return step(generator, seed)[0];
}

/**
 * Turns the given generator into an infinite stream of random values generated
 * with it.
 *
 * `seed` is the seed used to get the initial random value and start the
 * infinite sequence.
 *
 * If you don't care about the initial seed and reproducibility is not your
 * goal, you can use `to_random_yielder` which works like this function and
 * randomly picks the initial seed.
 */
export function to_yielder(generator, seed) {
  return $yielder.unfold(
    seed,
    (seed) => {
      let $ = step(generator, seed);
      let value;
      let new_seed;
      value = $[0];
      new_seed = $[1];
      return new $yielder.Next(value, new_seed);
    },
  );
}

/**
 * Turns the given generator into an infinite stream of random values generated
 * with it.
 *
 * The initial seed is chosen randomly so you won't have control over which
 * values are generated and may get different results each time you call this
 * function.
 *
 * If you want to have control over the initial seed used to get the infinite
 * sequence of values, you can use `to_yielder`.
 */
export function to_random_yielder(generator) {
  return to_yielder(generator, $seed.random());
}

/**
 * Generates a single value using the given generator.
 *
 * The initial seed is chosen randomly so you won't have control over which
 * value is generated and may get different results each time you call this
 * function.
 *
 * This is useful if you want to quickly get a value out of a generator and
 * do not care about reproducibility (if you want to decide which seed is
 * used for the generation process you'll have to use `random.step`).
 *
 * ## Examples
 *
 * Imagine you want to perform some action, say only 40% of the times.
 * Your code may look like this:
 *
 * ```gleam
 * let probability = random.float(0.0, 1.0)
 * case random.random_sample(probability) <= 0.4 {
 *   True -> perform_action()
 *   False -> Nil // do nothing
 * }
 * ```
 */
export function random_sample(generator) {
  let $ = $yielder.first(to_random_yielder(generator));
  let result;
  if ($ instanceof Ok) {
    result = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "prng/random",
      196,
      "random_sample",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 6431,
        end: 6498,
        pattern_start: 6442,
        pattern_end: 6452
      }
    )
  }
  return result;
}

function sort_ascending(one, other, compare) {
  let $ = compare(one, other);
  if ($ instanceof Lt) {
    return [one, other];
  } else if ($ instanceof Eq) {
    return [one, other];
  } else {
    return [other, one];
  }
}

/**
 * Generates integers in the given inclusive range.
 *
 * ## Examples
 *
 * Say you want to model the outcome of a dice, you could use `int` like this:
 *
 * ```gleam
 * let dice_roll = random.int(1, 6)
 * ```
 */
export function int(from, to) {
  return new Generator(
    (seed) => {
      let $ = sort_ascending(from, to, $int.compare);
      let low;
      let high;
      low = $[0];
      high = $[1];
      return random_int(seed, low, high);
    },
  );
}

/**
 * Generates floating point numbers in the given inclusive range.
 *
 * ## Examples
 *
 * ```gleam
 * let probability = random.float(0.0, 1.0)
 * ```
 */
export function float(from, to) {
  return new Generator(
    (seed) => {
      let $ = sort_ascending(from, to, $float.compare);
      let low;
      let high;
      low = $[0];
      high = $[1];
      return random_float(seed, low, high);
    },
  );
}

/**
 * Always generates the given value, no matter the seed used.
 *
 * ## Examples
 *
 * ```gleam
 * let always_eleven = random.constant(11)
 * random.random_sample(always_eleven)
 * // -> 11
 * ```
 */
export function constant(value) {
  return new Generator((seed) => { return [value, seed]; });
}

function get_by_weight(loop$first, loop$others, loop$countdown) {
  while (true) {
    let first = loop$first;
    let others = loop$others;
    let countdown = loop$countdown;
    let weight;
    let value;
    weight = first[0];
    value = first[1];
    if (others instanceof $Empty) {
      return value;
    } else {
      let second = others.head;
      let rest = others.tail;
      let positive_weight = $float.absolute_value(weight);
      let $ = $float.compare(countdown, positive_weight);
      if ($ instanceof Lt) {
        return value;
      } else if ($ instanceof Eq) {
        return value;
      } else {
        loop$first = second;
        loop$others = rest;
        loop$countdown = countdown - positive_weight;
      }
    }
  }
}

function do_fixed_size_list(loop$acc, loop$seed, loop$generator, loop$length) {
  while (true) {
    let acc = loop$acc;
    let seed = loop$seed;
    let generator = loop$generator;
    let length = loop$length;
    let $ = length <= 0;
    if ($) {
      return [acc, seed];
    } else {
      let $1 = step(generator, seed);
      let value;
      let seed$1;
      value = $1[0];
      seed$1 = $1[1];
      loop$acc = listPrepend(value, acc);
      loop$seed = seed$1;
      loop$generator = generator;
      loop$length = length - 1;
    }
  }
}

/**
 * Generates a lists of a fixed size; its values are generated using the
 * given generator.
 *
 * ## Examples
 *
 * Imagine you're modelling a game of
 * [Risk](https://en.wikipedia.org/wiki/Risk_(game)); when a player "attacks"
 * they can roll three dice. You may model that outcome using `fixed_size_list`
 * like this:
 *
 * ```gleam
 * let dice_roll = random.int(1, 6)
 * let attack_outcome = random.fixed_size_list(dice_roll, 3)
 * ```
 */
export function fixed_size_list(generator, length) {
  return new Generator(
    (seed) => { return do_fixed_size_list(toList([]), seed, generator, length); },
  );
}

/**
 * Transforms a generator into another one based on its generated values.
 *
 * The random value generated by the given generator is fed into the `do`
 * function and the returned generator is used as the new generator.
 *
 * ## Examples
 *
 * `then` is a really powerful function, almost all functions exposed by this
 * library could be defined in term of it!
 * Take as an example `map`, it can be implemented like this:
 *
 * ```gleam
 * fn map(generator: Generator(a), with fun: fn(a) -> b) -> Generator(b) {
 *   random.then(generator, fn(value) {
 *     random.constant(fun(value))
 *   })
 * }
 * ```
 *
 * Notice how the `do` function needs to return a `Generator(b)`, you can
 * achieve that by wrapping any constant value with the `random.constant`
 * generator.
 *
 * > Code written with `then` can gain a lot in readability if you use the
 * > `use` syntax, especially if it has some deep nesting. As an example, this
 * > is how you can rewrite the previous example taking advantage of `use`:
 * >
 * > ```gleam
 * > fn map(generator: Generator(a), with fun: fn(a) -> b) -> Generator(b) {
 * >   use value <- random.then(generator)
 * >   random.constant(fun(value))
 * > }
 * > ```
 */
export function then$(generator, generator_from) {
  return new Generator(
    (seed) => {
      let $ = step(generator, seed);
      let value;
      let seed$1;
      value = $[0];
      seed$1 = $[1];
      let _pipe = generator_from(value);
      return step(_pipe, seed$1);
    },
  );
}

/**
 * Generates a list with a random size with at most 32 items.
 * Each item is generated using the given generator.
 *
 * This is similar to `fixed_size_list` with the difference that the size
 * is chosen randomly.
 */
export function list(generator) {
  return then$(
    int(0, 32),
    (size) => { return fixed_size_list(generator, size); },
  );
}

/**
 * Transforms the values produced by a generator using the given function.
 *
 * ## Examples
 *
 * Imagine you want to make a generator for boolean values that returns
 * `True` and `False` with the same probability. You could do that using `map`
 * like this:
 *
 * ```gleam
 * let bool_generator = random.int(1, 2) |> random.map(fn(n) { n == 1 })
 * ```
 *
 * Here `map` allows you to transform the values produced by the initial
 * integer generator - either 1 or 2 - into boolean values: when the original
 * generator produces a 1, `bool_generator` will produce `True`; when the
 * original generator produces a 2, `bool_generator` will produce `False`.
 */
export function map(generator, fun) {
  return new Generator(
    (seed) => {
      let $ = step(generator, seed);
      let value;
      let seed$1;
      value = $[0];
      seed$1 = $[1];
      return [fun(value), seed$1];
    },
  );
}

/**
 * Generates values from the given ones with a weighted probability.
 *
 * This generator can guarantee to produce values since it always takes at
 * least one item (as its first argument); if it were to accept just a list of
 * options, it could be called like this:
 *
 * ```gleam
 * weighted([])
 * ```
 *
 * In which case it would be impossible to actually produce any value: none was
 * provided!
 *
 * ## Examples
 *
 * Given the following type to model the outcome of a coin flip:
 *
 * ```gleam
 * pub type CoinFlip {
 *   Heads
 *   Tails
 * }
 * ```
 *
 * You could write a generator for a loaded coin that lands on head 75% of the
 * times like this:
 *
 * ```gleam
 * let loaded_coin = random.weighted(#(0.75, Heads), [#(0.25, Tails)])
 * ```
 *
 * In this example the weights add up to 1, but you could use any number: the
 * weights get added up to a `total` and the probability of each option is its
 * `weight` / `total`.
 */
export function weighted(first, others) {
  let normalise = (pair) => { return $float.absolute_value($pair.first(pair)); };
  let total = normalise(first) + $float.sum($list.map(others, normalise));
  return map(
    float(0.0, total),
    (_capture) => { return get_by_weight(first, others, _capture); },
  );
}

/**
 * This function works exactly like `weighted` but will return an `Error(Nil)`
 * if the provided argument is an empty list since the generator wouldn't be
 * able to produce any value in that case.
 *
 * It generates values from the given list with a weighted probability.
 *
 * ## Examples
 *
 * ```gleam
 * random.try_weighted([])
 * // -> Error(Nil)
 * ```
 *
 * For example if you consider the following type definition to model color:
 *
 * ```gleam
 * type CoinFlip {
 *   Heads
 *   Tails
 * }
 * ```
 *
 * This call of `try_weighted` will produce a generator wrapped in an `Ok`:
 *
 * ```gleam
 * let assert Ok(coin_1) =
 *   random.try_weighted([#(0.75, Heads), #(0.25, Tails)])
 * let coin_2 = random.uniform(#(0.75, Heads), [#(0.25, Tails)])
 * ```
 *
 * The generators `coin_1` and `coin_2` will behave exactly the same.
 */
export function try_weighted(options) {
  if (options instanceof $Empty) {
    return new Error(undefined);
  } else {
    let first = options.head;
    let rest = options.tail;
    return new Ok(weighted(first, rest));
  }
}

/**
 * Combines two generators into a single one. The resulting generator produces
 * values obtained by applying `fun` to the values generated by the given
 * generators.
 *
 * ## Examples
 *
 * Imagine you need to generate random points in a 2D space:
 *
 * ```gleam
 * pub type Point {
 *   Point(x: Float, y: Float)
 * }
 * ```
 *
 * You can compose two basic generators into a `Point` generator using `map2`:
 *
 * ```gleam
 * let x_generator = random.float(-1.0, 1.0)
 * let y_generator = random.float(-1.0, 1.0)
 * let point_generator = map2(x_generator, y_generator, Point)
 * ```
 *
 * > Notice how you could get the same result using `then`:
 * >
 * > ```gleam
 * > pub fn point_generator() -> Generator(Point) {
 * >   use x <- random.then(random.float(-1.0, 1.0))
 * >   use y <- random.then(random.float(-1.0, 1.0))
 * >   random.constant(Point(x, y))
 * > }
 * > ```
 * >
 * > the `use` syntax paired with `then` may be confusing for other people
 * > reading your code, especially Gleam newcomers.
 * >
 * > Usually `map2`/`map3`/... will be more than enough if you just need to
 * > combine simple generators into more complex ones.
 */
export function map2(one, other, fun) {
  return new Generator(
    (seed) => {
      let $ = step(one, seed);
      let a;
      let seed$1;
      a = $[0];
      seed$1 = $[1];
      let $1 = step(other, seed$1);
      let b;
      let seed$2;
      b = $1[0];
      seed$2 = $1[1];
      return [fun(a, b), seed$2];
    },
  );
}

/**
 * Generates pairs of values obtained by combining the values produced by the
 * given generators.
 *
 * ## Examples
 *
 * ```gleam
 * let one_to_five = random.int(1, 5)
 * let probability = random.float(0.0, 1.0)
 * let ints_and_floats = random.pair(one_to_five, probability)
 *
 * random.random_sample(ints_and_floats)
 * // -> #(3, 0.22)
 * ```
 */
export function pair(one, other) {
  return map2(one, other, $pair.new$);
}

/**
 * Generates values from the given ones with an equal probability.
 *
 * This generator can guarantee to produce values since it always takes at
 * least one item (as its first argument); if it were to accept just a list of
 * options, it could be called like this:
 *
 * ```gleam
 * uniform([])
 * ```
 *
 * In which case it would be impossible to actually produce any value: none was
 * provided!
 *
 * ## Examples
 *
 * Given the following type to model colors:
 *
 * ```gleam
 * pub type Color {
 *   Red
 *   Green
 *   Blue
 * }
 * ```
 *
 * You could write a generator that returns each color with an equal
 * probability (~33%) each color like this:
 *
 * ```gleam
 * let color = random.uniform(Red, [Green, Blue])
 * ```
 */
export function uniform(first, others) {
  return weighted(
    [1.0, first],
    $list.map(others, (_capture) => { return $pair.new$(1.0, _capture); }),
  );
}

/**
 * This function works exactly like `uniform` but will return an `Error(Nil)`
 * if the provided argument is an empty list since the generator wouldn't be
 * able to produce any value in that case.
 *
 * It generates values from the given list with equal probability.
 *
 * ## Examples
 *
 * ```gleam
 * random.try_uniform([])
 * // -> Error(Nil)
 * ```
 *
 * For example if you consider the following type definition to model color:
 *
 * ```gleam
 * type Color {
 *   Red
 *   Green
 *   Blue
 * }
 * ```
 *
 * This call of `try_uniform` will produce a generator wrapped in an `Ok`:
 *
 * ```gleam
 * let assert Ok(color_1) = random.try_uniform([Red, Green, Blue])
 * let color_2 = random.uniform(Red, [Green, Blue])
 * ```
 *
 * The generators `color_1` and `color_2` will behave exactly the same.
 */
export function try_uniform(options) {
  if (options instanceof $Empty) {
    return new Error(undefined);
  } else {
    let first = options.head;
    let rest = options.tail;
    return new Ok(uniform(first, rest));
  }
}

/**
 * Generates two values with equal probability.
 *
 * This is a shorthand for `random.uniform(one, [other])`, but can read better
 * when there's only two choices.
 *
 * ## Examples
 *
 * Given the following type to model the outcome of a coin flip:
 *
 * ```gleam
 * pub type CoinFlip {
 *   Heads
 *   Tails
 * }
 * ```
 *
 * You can write a generator for coin flip outcomes like this:
 *
 * ```gleam
 * let flip = random.choose(Heads, Tails)
 * ```
 */
export function choose(one, other) {
  return uniform(one, toList([other]));
}

/**
 * Combines three generators into a single one. The resulting generator
 * produces values obtained by applying `fun` to the values generated by the
 * given generators.
 *
 * ## Examples
 *
 * Imagine you're writing a generator for random enemies in a game you're
 * making:
 *
 * ```gleam
 * pub type Enemy {
 *   Enemy(health: Int, attack: Int, defense: Int)
 * }
 * ```
 *
 * Each enemy starts with a random health (that can go from 50 to 100) and
 * random values for the `attack` and `defense` stats (each can be in a range
 * from 1 to 5):
 *
 * ```gleam
 * let health_generator = random.int(50, 100)
 * let attack_generator = random.int(1, 5)
 * let defense_generator = random.int(1, 5)
 *
 * let enemy_generator =
 *   random.map3(
 *     health_generator,
 *     attack_generator,
 *     defense_generator,
 *     Enemy,
 *   )
 * ```
 */
export function map3(one, two, three, fun) {
  return new Generator(
    (seed) => {
      let $ = step(one, seed);
      let a;
      let seed$1;
      a = $[0];
      seed$1 = $[1];
      let $1 = step(two, seed$1);
      let b;
      let seed$2;
      b = $1[0];
      seed$2 = $1[1];
      let $2 = step(three, seed$2);
      let c;
      let seed$3;
      c = $2[0];
      seed$3 = $2[1];
      return [fun(a, b, c), seed$3];
    },
  );
}

/**
 * Combines four generators into a single one. The resulting generator
 * produces values obtained by applying `fun` to the values generated by the
 * given generators.
 */
export function map4(one, two, three, four, fun) {
  return new Generator(
    (seed) => {
      let $ = step(one, seed);
      let a;
      let seed$1;
      a = $[0];
      seed$1 = $[1];
      let $1 = step(two, seed$1);
      let b;
      let seed$2;
      b = $1[0];
      seed$2 = $1[1];
      let $2 = step(three, seed$2);
      let c;
      let seed$3;
      c = $2[0];
      seed$3 = $2[1];
      let $3 = step(four, seed$3);
      let d;
      let seed$4;
      d = $3[0];
      seed$4 = $3[1];
      return [fun(a, b, c, d), seed$4];
    },
  );
}

/**
 * Combines five generators into a single one. The resulting generator
 * produces values obtained by applying `fun` to the values generated by the
 * given generators.
 *
 * > There's no `map6`, `map7`, and so on. If you feel like you need to compose
 * > together even more generators, you can use the `random.then` function.
 */
export function map5(one, two, three, four, five, fun) {
  return new Generator(
    (seed) => {
      let $ = step(one, seed);
      let a;
      let seed$1;
      a = $[0];
      seed$1 = $[1];
      let $1 = step(two, seed$1);
      let b;
      let seed$2;
      b = $1[0];
      seed$2 = $1[1];
      let $2 = step(three, seed$2);
      let c;
      let seed$3;
      c = $2[0];
      seed$3 = $2[1];
      let $3 = step(four, seed$3);
      let d;
      let seed$4;
      d = $3[0];
      seed$4 = $3[1];
      let $4 = step(five, seed$4);
      let e;
      let seed$5;
      e = $4[0];
      seed$5 = $4[1];
      return [fun(a, b, c, d, e), seed$5];
    },
  );
}

/**
 * Generates Strings with the given number number of UTF code points.
 *
 * > ⚠️ The generated codepoints will be in the range from 0 (inclusive) to
 * > 1023 (inclusive). If you feel like these strings are not enough for your
 * > needs, please open an issue! I'd love to hear your use case and improve
 * > the package.
 */
export function fixed_size_string(size) {
  let _pipe = fixed_size_list(utf_codepoint_in_range(0, 1023), size);
  return map(_pipe, $string.from_utf_codepoints);
}

/**
 * I'm not exposing this function because, if one is not careful with the range,
 * it might lead to a nasty infinite loop.
 * When I come up with a better alternative I might make a similar API public,
 * for now, if someone wants to do something unsafe they will have to
 * manually reimplement it.
 * 
 * @ignore
 */
function utf_codepoint_in_range(lower, upper) {
  return then$(
    int(lower, upper),
    (raw_codepoint) => {
      let $ = $string.utf_codepoint(raw_codepoint);
      if ($ instanceof Ok) {
        let codepoint = $[0];
        return constant(codepoint);
      } else {
        return utf_codepoint_in_range(lower, upper);
      }
    },
  );
}

/**
 * Generates Strings with a random number of UTF code points, between
 * 0 (included) and 32 (included).
 *
 * This is similar to `fixed_size_string`, with the difference that the
 * size is randomly generated as well.
 */
export function string() {
  return then$(int(0, 32), (size) => { return fixed_size_string(size); });
}

/**
 * Generates `BitArray`s with a random size.
 */
export function bit_array() {
  return map(string(), $bit_array.from_string);
}

/**
 * Generates a `Set(a)` where each item is generated using the provided
 * generator.
 *
 * This is similar to `fixed_size_set` with the difference that the set is
 * going to have a random size between 0 (inclusive) and 32 (inclusive).
 */
export function set(generator) {
  return then$(
    int(0, 32),
    (size) => { return fixed_size_set(generator, size); },
  );
}

/**
 * Generates a `Set(a)` where each item is generated using the provided
 * generator.
 *
 * > ⚠️ This function makes a best effort at generating a set with exactly the
 * > specified number of items, but beware that it may contain less items if
 * > the given generator cannot generate enough distinct values.
 */
export function fixed_size_set(generator, size) {
  let _pipe = $int.max(size, 0);
  return ((_capture) => {
    return do_fixed_size_set(generator, _capture, 0, 0, $set.new$());
  })(_pipe);
}

function do_fixed_size_set(
  generator,
  size,
  unique_items,
  consecutive_attempts,
  acc
) {
  let has_required_size = unique_items === size;
  return $bool.guard(
    has_required_size,
    constant(acc),
    () => {
      let has_reached_maximum_attempts = consecutive_attempts >= 10;
      return $bool.guard(
        has_reached_maximum_attempts,
        constant(acc),
        () => {
          return then$(
            generator,
            (item) => {
              let $ = $set.contains(acc, item);
              if ($) {
                let _pipe = (consecutive_attempts + 1);
                return ((_capture) => {
                  return do_fixed_size_set(
                    generator,
                    size,
                    unique_items,
                    _capture,
                    acc,
                  );
                })(_pipe);
              } else {
                let _pipe = $set.insert(acc, item);
                return ((_capture) => {
                  return do_fixed_size_set(
                    generator,
                    size,
                    unique_items + 1,
                    0,
                    _capture,
                  );
                })(_pipe);
              }
            },
          );
        },
      );
    },
  );
}

/**
 * Generates a `Map(k, v)` where each key value pair is generated using the
 * provided generators.
 *
 * This is similar to `fixed_size_dict` with the difference that the map is
 * going to have a random number of key-value pairs between 0 (inclusive) and
 * 32 (inclusive).
 */
export function dict(keys, values) {
  return then$(
    int(0, 32),
    (size) => { return fixed_size_dict(keys, values, size); },
  );
}

/**
 * Generates a `Dict(k, v)` where each key value pair is generated using the
 * provided generators.
 *
 * > ⚠️ This function makes a best effort at generating a map with exactly the
 * > specified number of keys, but beware that it may contain less items if
 * > the keys generator cannot generate enough distinct keys.
 */
export function fixed_size_dict(keys, values, size) {
  let _pipe = $int.max(size, 0);
  return ((_capture) => {
    return do_fixed_size_dict(keys, values, _capture, 0, 0, $dict.new$());
  })(_pipe);
}

function do_fixed_size_dict(
  keys,
  values,
  size,
  unique_keys,
  consecutive_attempts,
  acc
) {
  let has_required_size = unique_keys === size;
  return $bool.guard(
    has_required_size,
    constant(acc),
    () => {
      let has_reached_maximum_attempts = consecutive_attempts >= 10;
      return $bool.guard(
        has_reached_maximum_attempts,
        constant(acc),
        () => {
          return then$(
            keys,
            (key) => {
              let $ = $dict.has_key(acc, key);
              if ($) {
                let _pipe = (consecutive_attempts + 1);
                return ((_capture) => {
                  return do_fixed_size_dict(
                    keys,
                    values,
                    size,
                    unique_keys,
                    _capture,
                    acc,
                  );
                })(_pipe);
              } else {
                return then$(
                  values,
                  (value) => {
                    let _pipe = $dict.insert(acc, key, value);
                    return ((_capture) => {
                      return do_fixed_size_dict(
                        keys,
                        values,
                        size,
                        unique_keys + 1,
                        0,
                        _capture,
                      );
                    })(_pipe);
                  },
                );
              }
            },
          );
        },
      );
    },
  );
}
